import { ChangeEvent, FunctionComponent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { IconDefinition } from "@fortawesome/pro-regular-svg-icons";

import { Avatar } from "@material-tailwind/react";

import { SearchField } from "@/components/SearchField/SearchField";
import { classNames } from "@/utils/classNames";
import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

export interface LinkedinPersonSuggestionItem {
	urn: string;
	title: string;
	subtitle?: string;
	image?: string;
}

export interface LinkedinPersonSuggestionProps {
	handleSelect: (value?: LinkedinPersonSuggestionItem) => void;
	handleSearch?: (value: string) => void;
	selectedItem: LinkedinPersonSuggestionItem | undefined;
	data: LinkedinPersonSuggestionItem[];
	label?: string;
	testId?: string;
	floating?: boolean;
	icon?: IconDefinition;
	isDisabled?: boolean;
	isLoading?: boolean;
	showError?: boolean;
}

export const LinkedinPersonSuggestion: FunctionComponent<LinkedinPersonSuggestionProps> = ({
	handleSelect,
	handleSearch,
	selectedItem,
	data,
	label,
	testId,
	floating,
	isDisabled,
	isLoading,
	showError,
}) => {
	const { t } = useTranslation();
	const [searchValue, setSearchValue] = useState("");

	// Dropdown Logic
	const [showDropdown, setShowDropdown] = useState(false);
	const selectFieldRef = useRef<HTMLDivElement>(null);

	const handleSelectItem = (selected: LinkedinPersonSuggestionItem) => {
		handleSelect(selected);
		setShowDropdown(false);
	};

	const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
		const selectedItem = data.find((item) => item.title === e.target.value);

		handleSearch && handleSearch(e.target.value);

		setSearchValue(e.target.value);

		if (selectedItem) {
			handleSelect(selectedItem);
		}

		setShowDropdown(true);
	};

	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			if (!(selectFieldRef.current && selectFieldRef.current?.contains(target as Node))) {
				if (showDropdown) {
					setShowDropdown(false);
				}
			}
		};

		window.addEventListener("click", handleClick);

		return () => window.removeEventListener("click", handleClick);
	}, [selectFieldRef, showDropdown]);

	useEffect(() => {
		if (selectedItem) {
			setSearchValue(selectedItem.title);
		}
	}, [selectedItem]);

	const renderResults = () => {
		if (isLoading) {
			return <div className="flex items-center justify-center h-full">Loading...</div>;
		}

		if (!data.length) {
			return <p className="py-2.5 px-3 text-sm italic text-gray-700">{t("basics.noResults")}</p>;
		}

		return (
			<div className={data.length ? "border-b border-b-border z-100" : ""}>
				{data.map((item) => {
					const { urn, title, image, subtitle } = item;

					return (
						<button
							key={`option-select-${urn}`}
							className="flex w-full items-start gap-4 px-1 py-1 hover:bg-blue-action-200 cursor-pointer"
							onClick={() => handleSelectItem({ urn, title, image, subtitle })}
						>
							{image ? (
								<Avatar
									height={40}
									placeholder=""
									size="xs"
									src={image}
									variant="rounded"
									width={40}
									onPointerEnterCapture={() => {}}
									onPointerLeaveCapture={() => {}}
									onResize={() => {}}
									onResizeCapture={() => {}}
								/>
							) : (
								<div className="w-[24px] h-[24px]"></div>
							)}
							<div>
								<div className="text-left text-xs font-bold hover:text-red">{title}</div>
								<div className="text-left text-xs text-gray-700 font-normal">{subtitle}</div>
							</div>
						</button>
					);
				})}
			</div>
		);
	};

	const handleClickInput = () => setShowDropdown(!showDropdown);

	return (
		<div
			ref={selectFieldRef}
			className={classNames(
				"relative w-full mt-0",
				isDisabled ? "cursor-not-allowed opacity-[0.5]" : "cursor-pointer"
			)}
		>
			{label && (
				<label className="block mb-2 text-sm font-medium text-gray-900 appearance-none">
					{label}
				</label>
			)}
			<div className="relative">
				{handleSearch ? (
					<SearchField
						className="!top-[0px] !left-[0px] h-[40px] ml-1 mt-1"
						disabled={isDisabled}
						handleChange={handleSearchChange}
						handleReset={() => {
							setSearchValue("");
							handleSelect();
						}}
						icon={
							selectedItem?.image ? (
								<Avatar
									className="h-full mr-4"
									placeholder=""
									src={selectedItem.image}
									onPointerEnterCapture={() => {}}
									onPointerLeaveCapture={() => {}}
									onResize={() => {}}
									onResizeCapture={() => {}}
								/>
							) : undefined
						}
						isError={showError}
						name="searchForLinkedinPerson"
						placeholder={t("basics.pleaseSelect")}
						testId={testId}
						value={searchValue}
						onClick={handleClickInput}
					/>
				) : (
					""
				)}
			</div>
			{showDropdown && (
				<div
					className={classNames(
						"w-full mt-0.5 overflow-hidden bg-white border border-gray-200 rounded-md max-h-[220px] overflow-y-auto bb-scrollbar-darker",
						floating ? "absolute z-50" : "relative"
					)}
				>
					{renderResults()}
				</div>
			)}
		</div>
	);
};
